import React, { useEffect, useMemo, useState } from 'react';
import NamePet from './components/NamePet';
import PetScene from './components/PetScene';
import StatBar from './components/StatBar';
import { advancePet, clearPet, createPet, feedPet, getAgeLabel, getMood, loadPet, playWithPet, savePet } from './game/petState';
import { useInstallPrompt } from './hooks/useInstallPrompt';

const moodCopy = {
  happy: ['Spokojený', 'Všechno je v pořádku. Až podezřele.'],
  calm: ['V pohodě', 'Klidný malý život. Jen ho tak udrž.'],
  hungry: ['Hladový', 'Něco k jídlu by byl velmi dobrý nápad.'],
  sad: ['Osamělý', 'Na hračku už chvíli kouká dost smutně.'],
  sick: ['Slabý', 'Ukazatel zdraví opravdu není dekorace.'],
  dead: ['Mrtvý', 'Měl jsi jeden úkol.']
};

export default function App() {
  const [pet, setPet] = useState(() => {
    const stored = loadPet();
    if (!stored) return null;
    const updated = advancePet(stored);
    savePet(updated);
    return updated;
  });
  const [reactionId, setReactionId] = useState(0);
  const [toast, setToast] = useState('');
  const [now, setNow] = useState(Date.now());
  const { canInstall, installed, install } = useInstallPrompt();

  useEffect(() => {
    if (!pet) return undefined;
    const tick = () => {
      setNow(Date.now());
      setPet(current => {
        if (!current) return current;
        const updated = advancePet(current);
        savePet(updated);
        return updated;
      });
    };
    const interval = setInterval(tick, 15000);
    const visibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [Boolean(pet)]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(''), 1800);
    return () => clearTimeout(timeout);
  }, [toast]);

  const mood = useMemo(() => getMood(pet), [pet]);
  const copy = moodCopy[mood] || moodCopy.calm;

  const create = name => {
    const next = createPet(name);
    savePet(next);
    setPet(next);
    setReactionId(value => value + 1);
  };

  const feed = () => {
    const next = feedPet(pet);
    savePet(next);
    setPet(next);
    setReactionId(value => value + 1);
    setToast(`${next.name} právě zlikvidoval svačinu.`);
  };

  const play = () => {
    const next = playWithPet(pet);
    savePet(next);
    setPet(next);
    setReactionId(value => value + 1);
    setToast(`${next.name} má teď mnohem lepší náladu.`);
  };

  const restart = () => {
    clearPet();
    setPet(null);
  };

  if (!pet) return <NamePet onCreate={create} />;

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <div className="eyebrow">POCKET PET</div>
          <h1>{pet.name}</h1>
        </div>
        <div className="top-actions">
          {canInstall && <button className="ghost-button" onClick={install}>Nainstalovat</button>}
          {installed && <span className="installed-pill">Nainstalováno</span>}
        </div>
      </header>

      <section className="game-grid">
        <div className="pet-card">
          <div className="scene-copy">
            <span className={`mood-dot ${mood}`} />
            <div>
              <strong>{copy[0]}</strong>
              <span>{copy[1]}</span>
            </div>
          </div>
          <PetScene mood={mood} reactionId={reactionId} />
          <div className="drag-hint">Táhni pro otočení</div>
        </div>

        <aside className="panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">VITALS</span>
              <h2>Udrž {pet.name} naživu.</h2>
            </div>
            <div className="age-block">
              <span>VĚK</span>
              <strong>{getAgeLabel(pet, now)}</strong>
            </div>
          </div>

          <div className="stats">
            <StatBar icon="✦" label="Zdraví" value={pet.health} />
            <StatBar icon="●" label="Jídlo" value={pet.hunger} />
            <StatBar icon="◆" label="Nálada" value={pet.happiness} />
          </div>

          {pet.alive ? (
            <div className="actions">
              <button className="action-button feed" onClick={feed}>
                <span className="action-icon">🍗</span>
                <span><strong>Nakrm</strong><small>+34 jídlo</small></span>
              </button>
              <button className="action-button play" onClick={play}>
                <span className="action-icon">🪀</span>
                <span><strong>Hraj si</strong><small>+31 nálada · -7 jídlo</small></span>
              </button>
            </div>
          ) : (
            <div className="death-card">
              <strong>{pet.name} to nepřežil.</strong>
              <span>Narozen {new Date(pet.bornAt).toLocaleDateString('cs-CZ')} · žil {getAgeLabel(pet)}</span>
              <button onClick={restart}>Pořídit nového mazlíčka</button>
            </div>
          )}

          <div className="rules-card">
            <span className="eyebrow">JAK TO FUNGUJE</span>
            <p>Jídlo a nálada klesají i při zavřené aplikaci. Když spadnou příliš nízko, začne ubývat zdraví. Spokojený a nakrmený mazlíček se pomalu zotavuje.</p>
          </div>
        </aside>
      </section>
      {toast && <div className="toast">{toast}</div>}
    </main>
  );
}
